import React from 'react'
import { motion } from 'framer-motion'

export const VerpackGServicePage: React.FC = () => {

  const handleStartDeregistration = () => {
    // Navigate to the VerpackG service application
    window.location.href = '/verpackg-loschung/start'
  }

  const handleLearnMore = () => {
    // Scroll to features section
    const featuresSection = document.getElementById('features')
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleViewPricing = () => {
    // Scroll to pricing section
    const pricingSection = document.getElementById('pricing')
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const customContent = (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center">
        <motion.div
          className="inline-block p-4 bg-gradient-to-r from-red-100 to-rose-100 rounded-2xl mb-8"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-4xl">🗑️</span>
        </motion.div>
        
        <motion.h1
          className="text-5xl font-bold text-gray-900 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          VerpackG-Löschung Service
        </motion.h1>
        
        <motion.p
          className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Professional LUCID registry de-registration for foreign Amazon sellers. 
          Eliminate your ongoing German packaging compliance liability with our 
          automated bureaucracy handling service.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <button
            onClick={handleStartDeregistration}
            className="px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Start De-registration Process
          </button>
          <button
            onClick={handleLearnMore}
            className="px-8 py-4 bg-white text-red-600 font-bold rounded-xl border-2 border-red-600 hover:bg-red-50 transition-all duration-300"
          >
            Learn More
          </button>
        </motion.div>
      </section>

      {/* Problem Statement */}
      <section className="bg-gradient-to-r from-red-50 to-rose-50 rounded-2xl p-8 md:p-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-3xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Stop Worrying About German Compliance
          </motion.h2>
          
          <motion.div
            className="grid md:grid-cols-3 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-red-500 text-2xl mb-3">⚠️</div>
              <h3 className="font-semibold mb-2">Ongoing Liability</h3>
              <p className="text-sm text-gray-600">
                LUCID registrations create permanent compliance obligations
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-red-500 text-2xl mb-3">📋</div>
              <h3 className="font-semibold mb-2">Complex Bureaucracy</h3>
              <p className="text-sm text-gray-600">
                German de-registration requires authorized representation
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-red-500 text-2xl mb-3">💰</div>
              <h3 className="font-semibold mb-2">Hidden Costs</h3>
              <p className="text-sm text-gray-600">
                Ongoing zero-reports and potential penalties add up
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="features">
        <div className="text-center mb-12">
          <motion.h2
            className="text-3xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            How Our Service Works
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Complete de-registration in 3 simple steps
          </motion.p>
        </div>

        <motion.div
          className="grid md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-red-600">1</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Submit Documents</h3>
            <p className="text-gray-600">
              Upload your LUCID number, passport, and company details. 
              We validate everything within 24 hours.
            </p>
          </div>

          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-red-600">2</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">We Handle Everything</h3>
            <p className="text-gray-600">
              Our German representatives process your de-registration 
              using authorized power of attorney.
            </p>
          </div>

          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-red-600">3</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Complete Freedom</h3>
            <p className="text-gray-600">
              Receive official confirmation and eliminate all 
              future compliance obligations.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 md:p-12">
        <div className="text-center">
          <motion.h2
            className="text-3xl font-bold text-gray-900 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Simple, Transparent Pricing
          </motion.h2>

          <motion.div
            className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="text-center mb-6">
              <span className="text-4xl font-bold text-red-600">€390</span>
              <p className="text-gray-600 mt-2">One-time service fee</p>
            </div>

            <ul className="space-y-3 mb-8 text-left">
              <li className="flex items-center">
                <span className="text-green-500 mr-3">✓</span>
                Complete LUCID de-registration
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-3">✓</span>
                Authorized German representation
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-3">✓</span>
                Document validation & processing
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-3">✓</span>
                Legal compliance guarantee
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-3">✓</span>
                Money-back guarantee
              </li>
            </ul>

            <button
              onClick={handleStartDeregistration}
              className="w-full px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start De-registration Process
            </button>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="text-center mb-12">
          <motion.h2
            className="text-3xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Frequently Asked Questions
          </motion.h2>
        </div>

        <motion.div
          className="grid md:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="font-semibold mb-3">How long does the process take?</h3>
            <p className="text-gray-600">
              Typically 10-15 business days from document submission to official confirmation.
            </p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="font-semibold mb-3">What documents do I need?</h3>
            <p className="text-gray-600">
              Your LUCID registration number, passport copy, and current business address.
            </p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="font-semibold mb-3">Is this legally binding?</h3>
            <p className="text-gray-600">
              Yes, we use authorized German representation with proper power of attorney.
            </p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="font-semibold mb-3">What if it doesn't work?</h3>
            <p className="text-gray-600">
              We offer a full money-back guarantee if we cannot complete your de-registration.
            </p>
          </div>
        </motion.div>
      </section>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {customContent}
      <div className="flex gap-4 justify-center mt-8">
        <button 
          onClick={handleStartDeregistration}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Start De-registration Process
        </button>
        <button 
          onClick={handleViewPricing}
          className="px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
        >
          View Pricing
        </button>
      </div>
    </div>
  )
}